import { AreaDict } from './index';

/**
 * Documents area dictionary.
 *
 * Covers: DocumentsView (doc*), PortfolioReport (pr*), DocumentViewerModal (dvm*),
 * SurveyView (sv*), ChatbotView (cb*).
 *
 * NOTE ON DATA VALUES: document types ('Passport', 'OQOOD', 'SPA', 'SOA Document'…),
 * unit types ('Studio', '1 Bedroom'…), document categories ('Personal Documents',
 * 'Units', 'Other Documents') and every survey answer option are stored in Firestore
 * / posted to the API and compared in code. Only their DISPLAY labels live here — the
 * values themselves stay English in the components.
 */
export const documentsDict: AreaDict = {
  en: {
    // ==================== DOCUMENTS VIEW — upload card ====================
    docUploadTitle: 'Upload Document',
    docUploadDesc: 'Upload documents organized by category with automatic type detection',
    docCategory: 'Document Category',
    docCatPersonal: 'Personal Documents',
    docCatUnits: 'Unit Documents',
    docSelectUnit: 'Select Unit',
    docChooseUnit: 'Choose unit',
    docAddUnit: 'Add Unit',
    docType: 'Document Type',
    docTypePhPersonal: 'e.g., Passport, Visa, Emirates ID',
    docTypePhUnit: 'e.g., OTP Document, OQOOD, Payment Proof',
    docChooseFile: 'Choose File',
    docSelectUnitFirst: 'Please select a unit first or add a new unit',
    // ==================== DOCUMENTS VIEW — add unit form ====================
    docAddNewUnit: 'Add New Unit',
    docAddNewUnitDesc: 'Create a new unit to organize your documents',
    docUnitName: 'Unit Name',
    docUnitNamePh: 'e.g., Apartment 2A',
    docUnitNumber: 'Unit Number',
    docUnitNumberPh: 'e.g., A-201',
    docProjectName: 'Project Name',
    docProjectNamePh: 'e.g., Marina Heights',
    docUnitType: 'Unit Type',
    docStudio: 'Studio',
    doc1Bedroom: '1 Bedroom',
    doc2Bedroom: '2 Bedroom',
    doc3Bedroom: '3 Bedroom',
    docCreateUnit: 'Create Unit',
    docCancel: 'Cancel',
    // ==================== DOCUMENTS VIEW — library ====================
    docLibrary: 'Document Library',
    docLibraryDesc: 'Your documents organized by category',
    docExpandAll: 'Expand All',
    docCollapseAll: 'Collapse All',
    docNoneUploaded: 'No documents uploaded yet',
    docGrpPersonal: 'Personal Documents',
    docGrpOther: 'Other Documents',
    docGrpOtherHint: 'Documents not filed against one of your properties — uploaded without a property selected, or received by email before we could match them. Open one to see what it is; tell us which property it belongs to and we will file it there.',
    docClickCollapse: 'Click to collapse',
    docClickExpand: 'Click to expand',
    docView: 'View',
    docViewFileTitle: 'View file',
    docDownload: 'Download',
    docDownloadFileTitle: 'Download file',
    docDriveTitle: 'Open in Google Drive',
    // ==================== DOCUMENTS VIEW — extracted data ====================
    docExtractedInfo: 'Extracted Information',
    docAiProcessed: 'AI Processed',
    docFldName: 'Name',
    docFldPassport: 'Passport',
    docFldNationality: 'Nationality',
    docFldExpires: 'Expires',
    docFldId: 'ID',
    docFldType: 'Type',
    docFldOwner: 'Owner',
    docFldProject: 'Project',
    docFldUnit: 'Unit',
    docFldArea: 'Area',
    docFldPrice: 'Price',
    docFldPurchaser: 'Purchaser',
    docFldProperty: 'Property',
    docFldCompletion: 'Completion',
    docPurchasePrice: 'Purchase Price',
    docAddedToPortfolio: 'Property added to portfolio',
    docNotExtracted: 'Not extracted - Check document',
    docOqoodProcessed: 'OQOOD processed - Property added to portfolio',
    docExtractionIssue: 'Extraction Issue Detected',
    docExtractionIssueDesc: 'The system could not automatically extract the "Property Value" from your OQOOD document.',
    docEnterManually: 'Enter Data Manually',
    docReupload: 'Re-upload Document',
    docReuploadAlert: 'Please re-upload the document with better image quality',
    docSpaRecorded: 'Sales Purchase Agreement - Payment schedule and completion date recorded',
    docOrganized: 'Document organized (no portfolio impact)',
    docStored: 'Document stored and ready for viewing',
    docProcessingError: 'Processing Error',
    docProcessingErrorDesc: 'Document uploaded but automatic processing failed. The file has been saved and you can view it manually.',
    docProcessingAi: 'Processing with AI...',
    docExtracting: 'Extracting: Property Value, Project Name, Unit Details, Area',
    // ==================== DOCUMENTS VIEW — pop-out file viewer ====================
    docPopupBlocked: 'Popup blocked. Please allow popups for this site.',
    docViewerDownload: 'Download',
    docViewerDownloadPdf: 'Download PDF',
    docViewerPrint: 'Print',
    docViewerPrintPdf: 'Print PDF',
    docViewerClose: 'Close',
    docViewerLoadingPdf: 'Loading PDF...',
    docViewerNoPdfViewer: 'PDF Viewer Not Available',
    docViewerNoPdfViewerDesc: 'Your browser does not support inline PDF viewing.',
    docViewerFit: 'Fit',
    docViewerFitTitle: 'Fit to screen',
    docViewerActualSize: 'Actual size',
    docViewerZoomIn: 'Zoom In',
    docViewerZoomInTitle: 'Zoom in',
    docViewerZoomOut: 'Zoom Out',
    docViewerZoomOutTitle: 'Zoom out',
    docViewerDownloadImage: 'Download image',
    docViewerLoadingImage: 'Loading image...',
    docViewerImageFailed: 'Failed to load image',
    docViewerUnknownSize: 'Unknown size',
    docViewerDocument: 'Document',
    docViewerTypeLabel: 'Type',
    docViewerUnknown: 'Unknown',
    docViewerSizeLabel: 'Size',
    docViewerCannotPreview: 'This file type cannot be previewed in the browser.',
    docViewerCannotPreviewHint: 'Click download to save and open with an appropriate application.',
    docViewerDownloadFile: 'Download File',
    docErrorOpening: 'Error opening file. The file data might be corrupted. Please try re-uploading the document.',
    docNotAvailableView: 'File not available for viewing. Please re-upload the document.',
    docNotAvailableDownload: 'File not available for download. Please re-upload the document.',
    // ==================== DOCUMENTS VIEW — manual OQOOD entry ====================
    docOqoodInstrIntro: 'Please look at your OQOOD document and find:',
    docOqoodInstr1: '1. "Property Value" field (e.g., 2450000 AED)',
    docOqoodInstr2: '2. "Project Name" field (e.g., Seagate)',
    docOqoodInstr3: '3. "Property Name" field (e.g., Seagate Building 1/308)',
    docOqoodInstr4: '4. "Net Sold Area" field in Sq.M. (e.g., 122.47)',
    docOqoodPricePrompt1: 'Enter Purchase Price from "Property Value" field:',
    docOqoodPricePrompt2: '(Enter just the number, e.g., 2450000)',
    docOqoodPricePrompt3: 'Example: "Property Value: 2450000 AED" -> enter 2450000',
    docOqoodInvalidPrice: 'Invalid purchase price. Please enter a valid number.',
    docOqoodProjectPrompt1: 'Enter Project Name (Optional):',
    docOqoodProjectPrompt2: 'Example: "Project Name: Seagate" -> enter Seagate',
    docOqoodPropertyPrompt1: 'Enter Property/Unit from "Property Name" field (Optional):',
    docOqoodPropertyPrompt2: 'Example: "Property Name: Seagate Building 1/308" -> enter Seagate Building 1/308',
    docOqoodAreaPrompt1: 'Enter Net Sold Area in Sq.M. (Optional):',
    docOqoodAreaPrompt2: 'Example: "Net Sold Area: 122.47 Sq.M." -> enter 122.47',
    docOqoodUpdated: 'OQOOD data updated successfully!',
    docOqoodPortfolioNote: 'Your portfolio value will now reflect this property.',
    docOqoodUpdateFailed: 'Failed to update:',
    docOqoodUpdateError: 'Error updating OQOOD data:',

    // ==================== DOCUMENT VIEWER MODAL ====================
    dvmSave: 'Save',
    dvmClose: 'Close',
    dvmLoading: 'Loading…',
    dvmNoPreview: 'This file type cannot be previewed. Tap Save to download it.',
    dvmLoadFailed: 'Failed to load document',
    dvmPage: 'Page',
    dvmOf: 'of',
    dvmPrevPage: 'Previous page',
    dvmNextPage: 'Next page',

    // ==================== PORTFOLIO REPORT — masthead & hero ====================
    prKicker: 'Private investment portfolio',
    prTitleEm: 'Investment Portfolio',
    prEmpty: 'Your report appears as soon as the first unit is registered to your account.',
    prEmptyAllSold: 'Every unit in this portfolio has been sold. The realised result is below.',
    prTagline: 'The place where your imagination creates your reality.',
    prMetaReport: 'Investment Portfolio Report',
    prMetaCurrencies: 'Currencies',
    prMetaConfidential: 'Private & Confidential',
    prCurFirst: 'first',
    prHeroWaiting: 'Dubai is waiting',
    prHeroH: 'Your money works for you. Around the clock.',
    prEarnedToday: 'Earned today',
    // ==================== PORTFOLIO REPORT — section titles ====================
    prSec1: 'Investment portfolio at a glance',
    prSec2: 'Capital deployment',
    prSec3: 'Object register',
    prSec4: 'Payments per year',
    prSec5: 'Acquisition pipeline',
    prSec6: 'Projection until the end of',
    prSecSales: 'Realised sales',
    // ==================== PORTFOLIO REPORT — realised sales ====================
    prIncomeFromSelling: 'Income from selling',
    prUnitSold: 'unit sold',
    prUnitsSold: 'units sold',
    prSoldLede: 'sold.',
    prRealisedProfit: 'Realised profit',
    prRealisedProfitLower: 'realised profit',
    prRealisedRoiLower: 'realised return',
    prSaleProceedsGross: 'Sale price total',
    prSellingCosts: 'Selling costs',
    prNetProceeds: 'Net proceeds',
    prAfterCosts: 'after transaction costs',
    prOfSalePrice: 'of the sale price',
    prDldCommissionTrustee: 'DLD, commission & trustee',
    prCapitalReturned: 'Capital returned',
    prAtPurchasePrice: 'at purchase price',
    prRealisedNote: 'A sold unit is no longer a holding, so it is left out of the figures above — its capital and its profit are reported here instead.',
    prColSold: 'Sold',
    prColHeld: 'Held',
    prColSalePrice: 'Sale price',
    prColCosts: 'Costs',
    prColNetProfit: 'Net profit',
    prMonthsShort: 'mo.',
    prDldTransferFee: 'DLD transfer fee',
    prAgencyCommission: 'Agency commission',
    prTrusteeFee: 'Trustee fee',
    prOtherExpenses: 'Other expenses',
    prNoPurchaseBasis: 'no purchase price on record',
    prGrossProfit: 'Gross profit',
    prBeforeCosts: 'before selling costs',
    prAnnualisedRoi: 'Annualised ROI',
    prSaleDate: 'Sale date',
    prSalesFoot: 'ROI = (net proceeds − purchase price) ÷ purchase price. Net proceeds are the sale price less the DLD transfer fee, the agency commission, the trustee fee and any other expenses recorded against the sale. The annualised column is a simple annualisation of that return over the holding period, shown from three months of holding onward.',
    prTapSale: 'Tap a sale for the full cost breakdown.',
    // ---- off-plan resale (assignment of the SPA before handover)
    prAssignmentLabel: 'off-plan assignment',
    prOfWhichAssignmentOne: 'One of them was an off-plan assignment: the buyer took over the remaining installments, so its return is measured against the capital actually paid in.',
    prOfWhichAssignmentMany: 'Some of them were off-plan assignments: the buyer took over the remaining installments, so their return is measured against the capital actually paid in.',
    prColCapitalIn: 'Capital in',
    prCapitalPaidIn: 'Capital paid in',
    prCapitalAtRisk: 'capital actually at risk',
    prOfSpaPrice: 'of an SPA price of',
    prOnSpaPrice: 'on the SPA price',
    prOnEntryPrice: 'on your entry price',
    prCashReceived: 'Cash received',
    prOutstandingAssumed: 'Outstanding balance assumed by the buyer',
    prBalanceToDeveloper: 'went to the developer',
    prNocFee: 'Developer NOC fee',
    prDeveloperAdminFee: 'Developer admin fee',
    prPaidPreviousOwner: 'Paid to the previous owner',
    prInstallmentsSince: 'Installments paid since taking over',
    prTakenOverLabel: 'taken over from an earlier owner',
    prSalesFootAssignment: 'On an off-plan assignment there is no title yet, so the SPA changes hands: the buyer takes over the balance owed to the developer and pays the equity plus premium. Cash received is therefore the price less that balance and the selling costs, and the return is measured against the installments actually paid in — never the full SPA price.',
    prSaleNoBasisOne: 'sale is listed without a purchase price and is left out of the profit total.',
    prSaleNoBasisMany: 'sales are listed without a purchase price and are left out of the profit total.',
    // ==================== PORTFOLIO REPORT — at a glance ====================
    prTotalInvestment: 'Total investment',
    prPropsAtPurchase: 'properties at purchase price',
    prAnnualNetRent: 'Annual net rent',
    prAnnualNetRentLower: 'annual net rent',
    prNoRentedYet: 'No rented units yet',
    prSignedLease: 'signed lease',
    prSignedLeases: 'signed leases',
    prRentTargetProgress: 'Rent target progress',
    prSecured: 'Secured',
    prRentedProperty: 'rented property',
    prRentedProperties: 'rented properties',
    prUnitsInPortfolio: 'Units in the investment portfolio',
    prCompletedLower: 'completed',
    prOffPlanLower: 'off-plan',
    prProgressTowardsRent: 'Progress towards the rental target of',
    prItems: 'items',
    prOpenLegend: 'Open',
    prCongrats: 'Congratulations',
    prIsLeased: 'is leased.',
    prAnnualRentSecured: 'annual rent secured',
    prOf: 'of',
    prCompletedUnit: 'completed unit',
    prCompletedUnits: 'completed units',
    prNowLeased: 'now leased.',
    prNextPayment: 'Next payment',
    prDue: 'due',
    prDivisionPaidOpen: 'Division · paid vs open',
    prAlreadyPaid: 'Already paid',
    prOutstanding: 'Outstanding payments & transfers',
    prPaymentSplitHint: 'Payment split appears once installment plans have been extracted from your OTP / SPA documents.',
    prMixByPurchase: 'Investment portfolio mix by purchase price',
    prObjects: 'objects',
    prOffPlanCap: 'Off-plan',
    prCompletedCap: 'Completed',
    // ==================== PORTFOLIO REPORT — capital deployment ====================
    prCommitted: 'committed.',
    prToGoSentence: 'to go.',
    prProgressTowardsTarget: 'Progress towards the target of',
    prInvestedNotarised: 'Invested & notarised',
    prAvailable: 'Available',
    prMilestonesCapital: 'Milestones · capital deployed',
    prDeployed: 'deployed',
    prAchieved: 'achieved',
    prOpenState: 'open',
    prMilestonesRent: 'Milestones · annual rental income (net)',
    prRentPa: 'rent pa',
    prEarnsLive: 'Your investment portfolio earns — live',
    prAccruedToday: 'Contracted rent accrued so far today',
    prPerDay: 'per day',
    prPerMonthAvg: 'per month (avg)',
    prPerDayAtPre: 'per day at the',
    prPerDayAtPost: 'target',
    prNextMilestone: 'Next milestone',
    prInvestedWord: 'invested',
    prToGoShort: 'to go',
    prNextRentalTarget: 'Next rental target',
    prPa: 'pa',
    prBasisPre: 'Basis: annual rent from your',
    prSignedTenancy: 'signed tenancy contract',
    prSignedTenancies: 'signed tenancy contracts',
    prBasisAccrued: 'accrued through the current time.',
    prBasisGross: 'Gross of service charges & fees · converted at',
    prPerEur: 'per EUR',
    // ==================== PORTFOLIO REPORT — achievements ====================
    prMilestonesAchievements: 'Milestones & achievements',
    prUnlocked: 'unlocked',
    prBadgeTrophy: 'Trophy asset',
    prSecuredSuffix: 'secured',
    prBadgeLargestUnit: 'Largest unit secured',
    prBadgePortfolioBasis: 'Portfolio basis',
    prUnit: 'unit',
    prUnits: 'units',
    prAcquired: 'acquired',
    prBadgeCommercial: 'Commercial debut',
    prBadgeOfficeVilla: 'Office / villa purchase',
    prFirst: 'First',
    prBadgeFirstRent: 'First rent',
    prPropertySing: 'property',
    prPropertyPlur: 'properties',
    prRentedSuffix: 'rented',
    prRentTarget: 'Rent target',
    prBadgeHalfTime: 'Half-time',
    prMission: 'Mission',
    prTargetReached: 'target reached',
    // ==================== PORTFOLIO REPORT — object register ====================
    prOneSkyline: 'One skyline. One goal.',
    prColObject: 'Object',
    prColUnit: 'Unit',
    prColType: 'Type',
    prColPosition: 'Position',
    prColHandover: 'Handover',
    prColPurchase: 'Purchase',
    prColStatus: 'Status',
    prProperty: 'Property',
    prFinishedRented: 'Finished · rented',
    prFinishedVacant: 'Finished · vacant',
    prArea: 'Area',
    prPricePerSqft: 'Price / sqft',
    prPurchased: 'Purchased',
    prMarketValueDld: 'Market value (DLD)',
    prMarketValue: 'Market value',
    prNoDldComps: 'purchase price — no DLD comparables',
    prUnrealisedGain: 'Unrealised gain',
    prRentPerMonth: 'Rent / month',
    prEst: 'est.',
    prGrossYield: 'Gross yield',
    prTenant: 'Tenant',
    prLeased: 'Leased',
    prRentCollected: 'Rent collected',
    prExpensesActual: 'Expenses (actual)',
    prNetRent: 'Net rent',
    prInTotal: 'In total',
    prTapObject: 'Tap an object to see area, valuation and rent details.',
    // ==================== PORTFOLIO REPORT — payments ====================
    prColYear: 'Year',
    prColCount: 'Count',
    prColAmount: 'Amount',
    prColPaidOfWhich: 'Paid of which',
    prColStillOpen: 'Still open',
    prInstallments: 'Installments',
    prPaid: 'Paid',
    prTapYear: 'Tap a year to see the breakdown of all installments.',
    prNext5: 'Next 5 payments',
    prColDue: 'Due',
    prColRate: 'Rate',
    // ==================== PORTFOLIO REPORT — pipeline ====================
    prSegment: 'Segment',
    prPhase: 'Phase',
    prReviewing: 'Reviewing',
    prOffer: 'Offer',
    prNetReturnLower: 'net return',
    prNextSteps: 'Next steps',
    // ==================== PORTFOLIO REPORT — projection ====================
    prUntilDecember: 'Until December:',
    prRentWord: 'rent.',
    prPerDaySentence: 'Per day.',
    prInvestedByEnd: 'Invested by the end of',
    prAnnualRentAtEnd: 'Annual rent at the end of',
    prMonthlyRent: 'Monthly rent',
    prDailyRent: 'Daily rent',
    prColMonth: 'Month',
    prCapitalInvested: 'Capital invested',
    prCumulativeInvestments: 'Cumulative investments',
    prAnnualRent: 'Annual rent',
    prCumulative: 'Cumulative',
    prBaseline: 'Baseline',
    prFootRest: 'rent from signed leases · rent shown is your actual Property Management lease income only — units without a signed lease contribute nothing until they are leased · invested capital phases in from your scheduled payment-plan installments.',
    prLtPre: 'At',
    prLtMid: 'and',
    prLtGoal: 'the target of',
    prReachedIn: 'Reached in',
    prInSight: 'In sight.',
    prColPortfolio: 'Investment portfolio',
    prColNetReturn: 'Net return',
    prGoalAchieved: 'Goal achieved',
    prAssumesPre: 'Assumes the portfolio is fully deployed to',
    prAssumesRises: '· net yield rises',
    prAssumesPoints: 'percentage points per year from',
    prAssumesTo: 'to',
    prAssumesThrough: 'through rent reviews, new lettings and optimisation · at a constant',
    prAssumesStays: 'the rent stays at',
    prAssumesStep: 'the step up to',
    prAssumesEnd: 'comes from the yield.',
    prFooterPre: 'Figures converted at',
    prFooterPost: 'per EUR · market values from Dubai Land Department records where available · rent from signed tenancy contracts where a lease exists.',

    // ==================== SURVEY VIEW ====================
    svHeaderTitle: "We'd Love Your Feedback",
    svHeaderSub: 'Help us build a better app for you — takes about 3-4 minutes',
    svProgress: 'Progress',
    svOf: 'of',
    svSection1: 'Your Usage',
    svSection2: 'Pain Points',
    svSection3: "What's Next",
    svFmtOne: 'Choose one',
    svFmtMulti: 'Select all that apply',
    svFmtRate: 'Rate 1–5',
    svFmtRank: 'Rank in order',
    svFmtVoice: 'Type or record a voice message',
    svQ1: 'How many properties do you currently manage in the app?',
    svQ1Opt4: 'More than 5',
    svQ2: 'Which parts of the app have you used already?',
    svQ2Opt1: 'Viewing my property portfolio',
    svQ2Opt2: 'Uploading documents',
    svQ2Opt3: 'AI assistant',
    svQ2Opt4: 'Payment schedule tracking',
    svQ2Opt5: 'Market reports',
    svQ3: 'How easy is it for you to manage your Dubai property with the app today?',
    svRateLow: 'Very difficult',
    svRateHigh: 'Very easy',
    svQ4: 'Which of these tasks feels most difficult or inconvenient today?',
    svQ4Opt1: 'Uploading documents',
    svQ4Opt2: 'Understanding my portfolio at a glance',
    svQ4Opt3: 'Keeping track of insurance and ownership paperwork',
    svQ4Opt4: 'Reviewing potential new properties',
    svQ4Opt5: 'Knowing what to do next as an owner',
    svRankHint: 'Tap each item to rank it (most important first):',
    svQ5: 'Please tell us about the biggest challenge you face when managing your property.',
    svVoicePlaceholder: 'Type your answer or tap the microphone to speak...',
    svStopRecording: 'Stop Recording',
    svTranscribing: 'Transcribing...',
    svRecordVoice: 'Tap to record voice message',
    svQ6: 'What would you most like the app to help you with next?',
    svQ6Opt1: 'Better overview of all my properties',
    svQ6Opt2: 'Simpler document organization',
    svQ6Opt3: 'Reminders for important deadlines or renewals',
    svQ6Opt4: 'Better insights before buying another property',
    svQ6Opt5: 'Resale support',
    svQ7: 'If the app added one new feature in the next 6 months, which should it be?',
    svQ7Opt1: 'Better portfolio dashboard',
    svQ7Opt2: 'Document reminders and alerts',
    svQ7Opt3: 'Resale support',
    svQ7Opt4: 'Property comparison tools',
    svQ7Opt5: 'Advisor support / chat',
    svOther: 'Other',
    svSpecify: 'Please specify...',
    svQ8: 'Which improvement would save you the most time or effort personally?',
    svQ8Opt1: 'Portfolio overview',
    svQ8Opt2: 'Documents and reminders',
    svQ8Opt3: 'Buying support',
    svQ8Opt4: 'Selling support',
    svQ8Opt5: 'Rental support',
    svQ9: 'Is there anything missing in the app that would make property ownership easier for you?',
    svSubmitting: 'Submitting...',
    svSubmitSurvey: 'Submit Survey',
    svConfidential: 'Your responses are confidential and will only be used to improve the app.',
    svThankYou: 'Thank You!',
    svThankYouDesc: 'Your feedback is incredibly valuable and will directly shape the next features we build.',
    svRecorded: 'Your response has been recorded',
    svMicDenied: 'Please allow microphone access to record a voice message.',
    svMicUnsupported: 'Voice recording is not supported on this device.',
    svSubmitFailed: 'Failed to submit survey',
    svGenericError: 'Something went wrong. Please try again.',

    // ==================== CHATBOT VIEW ====================
    cbTitle: 'Dubai Real Estate AI Assistant',
    cbSubtitle: 'Get expert insights about Dubai property market, investment opportunities, and regulations',
    cbThinking: 'AI is thinking...',
    cbQuickQuestions: 'Quick Questions:',
    cbQuick1: 'What are the best areas to invest in Dubai?',
    cbQuick2: 'How can I get a visa through property investment?',
    cbQuick3: 'What are the current rental yields?',
    cbQuick4: 'Tell me about off-plan properties',
    cbPlaceholder: 'Ask about Dubai real estate...',
    cbSend: 'Send',
    cbHighlights: 'Market Highlights',
    cbHighlightsSub: 'Latest trends and insights',
    cbGrowthTitle: 'Investment Growth',
    cbGrowthDesc: 'Dubai property market shows strong growth with increasing foreign investment',
    cbVisaDesc: 'Long-term residency available for property investments above AED 2M',
    cbYieldTitle: 'Rental Yields',
    cbYieldDesc: 'Competitive rental returns averaging 5-8% across prime locations',
  },
  de: {
    // ==================== DOCUMENTS VIEW — upload card ====================
    docUploadTitle: 'Dokument hochladen',
    docUploadDesc: 'Laden Sie Dokumente nach Kategorie geordnet hoch – der Typ wird automatisch erkannt',
    docCategory: 'Dokumentenkategorie',
    docCatPersonal: 'Persönliche Dokumente',
    docCatUnits: 'Dokumente zur Einheit',
    docSelectUnit: 'Einheit auswählen',
    docChooseUnit: 'Einheit wählen',
    docAddUnit: 'Einheit hinzufügen',
    docType: 'Dokumententyp',
    docTypePhPersonal: 'z. B. Passport, Visa, Emirates ID',
    docTypePhUnit: 'z. B. OTP Document, OQOOD, Payment Proof',
    docChooseFile: 'Datei auswählen',
    docSelectUnitFirst: 'Bitte wählen Sie zuerst eine Einheit aus oder fügen Sie eine neue hinzu',
    // ==================== DOCUMENTS VIEW — add unit form ====================
    docAddNewUnit: 'Neue Einheit hinzufügen',
    docAddNewUnitDesc: 'Erstellen Sie eine neue Einheit, um Ihre Dokumente zu ordnen',
    docUnitName: 'Name der Einheit',
    docUnitNamePh: 'z. B. Apartment 2A',
    docUnitNumber: 'Einheitsnummer',
    docUnitNumberPh: 'z. B. A-201',
    docProjectName: 'Projektname',
    docProjectNamePh: 'z. B. Marina Heights',
    docUnitType: 'Typ der Einheit',
    docStudio: 'Studio',
    doc1Bedroom: '1 Schlafzimmer',
    doc2Bedroom: '2 Schlafzimmer',
    doc3Bedroom: '3 Schlafzimmer',
    docCreateUnit: 'Einheit erstellen',
    docCancel: 'Abbrechen',
    // ==================== DOCUMENTS VIEW — library ====================
    docLibrary: 'Dokumentenarchiv',
    docLibraryDesc: 'Ihre Dokumente nach Kategorie geordnet',
    docExpandAll: 'Alle ausklappen',
    docCollapseAll: 'Alle einklappen',
    docNoneUploaded: 'Noch keine Dokumente hochgeladen',
    docGrpPersonal: 'Persönliche Dokumente',
    docGrpOther: 'Sonstige Dokumente',
    docGrpOtherHint: 'Dokumente, die keiner Ihrer Immobilien zugeordnet sind — ohne Immobilienauswahl hochgeladen oder per E-Mail eingegangen, bevor eine Zuordnung möglich war. Öffnen Sie eines, um zu sehen, worum es sich handelt; sagen Sie uns, zu welcher Immobilie es gehört, und wir ordnen es zu.',
    docClickCollapse: 'Zum Einklappen klicken',
    docClickExpand: 'Zum Ausklappen klicken',
    docView: 'Ansehen',
    docViewFileTitle: 'Datei ansehen',
    docDownload: 'Herunterladen',
    docDownloadFileTitle: 'Datei herunterladen',
    docDriveTitle: 'In Google Drive öffnen',
    // ==================== DOCUMENTS VIEW — extracted data ====================
    docExtractedInfo: 'Extrahierte Informationen',
    docAiProcessed: 'KI-verarbeitet',
    docFldName: 'Name',
    docFldPassport: 'Pass',
    docFldNationality: 'Staatsangehörigkeit',
    docFldExpires: 'Gültig bis',
    docFldId: 'ID',
    docFldType: 'Typ',
    docFldOwner: 'Eigentümer',
    docFldProject: 'Projekt',
    docFldUnit: 'Einheit',
    docFldArea: 'Fläche',
    docFldPrice: 'Preis',
    docFldPurchaser: 'Käufer',
    docFldProperty: 'Immobilie',
    docFldCompletion: 'Fertigstellung',
    docPurchasePrice: 'Kaufpreis',
    docAddedToPortfolio: 'Immobilie zum Portfolio hinzugefügt',
    docNotExtracted: 'Nicht erkannt – bitte Dokument prüfen',
    docOqoodProcessed: 'OQOOD verarbeitet – Immobilie zum Portfolio hinzugefügt',
    docExtractionIssue: 'Problem bei der Datenerkennung',
    docExtractionIssueDesc: 'Der Wert im Feld "Property Value" konnte nicht automatisch aus Ihrem OQOOD-Dokument gelesen werden.',
    docEnterManually: 'Daten manuell eingeben',
    docReupload: 'Dokument erneut hochladen',
    docReuploadAlert: 'Bitte laden Sie das Dokument in besserer Bildqualität erneut hoch',
    docSpaRecorded: 'Kaufvertrag – Zahlungsplan und Fertigstellungstermin erfasst',
    docOrganized: 'Dokument eingeordnet (keine Auswirkung auf das Portfolio)',
    docStored: 'Dokument gespeichert und zur Ansicht bereit',
    docProcessingError: 'Verarbeitungsfehler',
    docProcessingErrorDesc: 'Das Dokument wurde hochgeladen, die automatische Verarbeitung ist jedoch fehlgeschlagen. Die Datei ist gespeichert und kann manuell angesehen werden.',
    docProcessingAi: 'Verarbeitung mit KI ...',
    docExtracting: 'Es werden gelesen: Kaufpreis, Projektname, Angaben zur Einheit, Fläche',
    // ==================== DOCUMENTS VIEW — pop-out file viewer ====================
    docPopupBlocked: 'Pop-up blockiert. Bitte erlauben Sie Pop-ups für diese Seite.',
    docViewerDownload: 'Herunterladen',
    docViewerDownloadPdf: 'PDF herunterladen',
    docViewerPrint: 'Drucken',
    docViewerPrintPdf: 'PDF drucken',
    docViewerClose: 'Schließen',
    docViewerLoadingPdf: 'PDF wird geladen ...',
    docViewerNoPdfViewer: 'PDF-Anzeige nicht verfügbar',
    docViewerNoPdfViewerDesc: 'Ihr Browser unterstützt die direkte PDF-Anzeige nicht.',
    docViewerFit: 'Anpassen',
    docViewerFitTitle: 'An Bildschirm anpassen',
    docViewerActualSize: 'Originalgröße',
    docViewerZoomIn: 'Vergrößern',
    docViewerZoomInTitle: 'Vergrößern',
    docViewerZoomOut: 'Verkleinern',
    docViewerZoomOutTitle: 'Verkleinern',
    docViewerDownloadImage: 'Bild herunterladen',
    docViewerLoadingImage: 'Bild wird geladen ...',
    docViewerImageFailed: 'Bild konnte nicht geladen werden',
    docViewerUnknownSize: 'Größe unbekannt',
    docViewerDocument: 'Dokument',
    docViewerTypeLabel: 'Typ',
    docViewerUnknown: 'Unbekannt',
    docViewerSizeLabel: 'Größe',
    docViewerCannotPreview: 'Dieser Dateityp kann im Browser nicht angezeigt werden.',
    docViewerCannotPreviewHint: 'Laden Sie die Datei herunter und öffnen Sie sie mit einem geeigneten Programm.',
    docViewerDownloadFile: 'Datei herunterladen',
    docErrorOpening: 'Die Datei konnte nicht geöffnet werden. Möglicherweise sind die Dateidaten beschädigt. Bitte laden Sie das Dokument erneut hoch.',
    docNotAvailableView: 'Die Datei steht nicht zur Ansicht bereit. Bitte laden Sie das Dokument erneut hoch.',
    docNotAvailableDownload: 'Die Datei steht nicht zum Herunterladen bereit. Bitte laden Sie das Dokument erneut hoch.',
    // ==================== DOCUMENTS VIEW — manual OQOOD entry ====================
    // Field names in quotes stay English — they are printed that way on the OQOOD document.
    docOqoodInstrIntro: 'Bitte sehen Sie in Ihrem OQOOD-Dokument nach und suchen Sie:',
    docOqoodInstr1: '1. Feld "Property Value" (z. B. 2450000 AED)',
    docOqoodInstr2: '2. Feld "Project Name" (z. B. Seagate)',
    docOqoodInstr3: '3. Feld "Property Name" (z. B. Seagate Building 1/308)',
    docOqoodInstr4: '4. Feld "Net Sold Area" in Sq.M. (z. B. 122.47)',
    docOqoodPricePrompt1: 'Kaufpreis aus dem Feld "Property Value" eingeben:',
    docOqoodPricePrompt2: '(Nur die Zahl eingeben, z. B. 2450000)',
    docOqoodPricePrompt3: 'Beispiel: "Property Value: 2450000 AED" -> 2450000 eingeben',
    docOqoodInvalidPrice: 'Ungültiger Kaufpreis. Bitte geben Sie eine gültige Zahl ein.',
    docOqoodProjectPrompt1: 'Projektname eingeben (optional):',
    docOqoodProjectPrompt2: 'Beispiel: "Project Name: Seagate" -> Seagate eingeben',
    docOqoodPropertyPrompt1: 'Immobilie/Einheit aus dem Feld "Property Name" eingeben (optional):',
    docOqoodPropertyPrompt2: 'Beispiel: "Property Name: Seagate Building 1/308" -> Seagate Building 1/308 eingeben',
    docOqoodAreaPrompt1: 'Netto-Verkaufsfläche in Sq.M. eingeben (optional):',
    docOqoodAreaPrompt2: 'Beispiel: "Net Sold Area: 122.47 Sq.M." -> 122.47 eingeben',
    docOqoodUpdated: 'OQOOD-Daten erfolgreich aktualisiert!',
    docOqoodPortfolioNote: 'Ihr Portfoliowert berücksichtigt diese Immobilie nun.',
    docOqoodUpdateFailed: 'Aktualisierung fehlgeschlagen:',
    docOqoodUpdateError: 'Fehler beim Aktualisieren der OQOOD-Daten:',

    // ==================== DOCUMENT VIEWER MODAL ====================
    dvmSave: 'Speichern',
    dvmClose: 'Schließen',
    dvmLoading: 'Wird geladen …',
    dvmNoPreview: 'Dieser Dateityp kann nicht angezeigt werden. Tippen Sie auf Speichern, um ihn herunterzuladen.',
    dvmLoadFailed: 'Dokument konnte nicht geladen werden',
    dvmPage: 'Seite',
    dvmOf: 'von',
    dvmPrevPage: 'Vorherige Seite',
    dvmNextPage: 'Nächste Seite',

    // ==================== PORTFOLIO REPORT — masthead & hero ====================
    prKicker: 'Privates Investmentportfolio',
    prTitleEm: 'Investmentportfolio',
    prEmpty: 'Ihr Bericht erscheint, sobald die erste Einheit Ihrem Konto zugeordnet ist.',
    prEmptyAllSold: 'Alle Einheiten dieses Portfolios sind verkauft. Das realisierte Ergebnis finden Sie unten.',
    prTagline: 'Der Ort, an dem Ihre Vorstellung zu Ihrer Wirklichkeit wird.',
    prMetaReport: 'Bericht zum Investmentportfolio',
    prMetaCurrencies: 'Währungen',
    prMetaConfidential: 'Privat & vertraulich',
    prCurFirst: 'zuerst',
    prHeroWaiting: 'Dubai wartet',
    prHeroH: 'Ihr Geld arbeitet für Sie. Rund um die Uhr.',
    prEarnedToday: 'Heute verdient',
    // ==================== PORTFOLIO REPORT — section titles ====================
    prSec1: 'Investmentportfolio auf einen Blick',
    prSec2: 'Kapitaleinsatz',
    prSec3: 'Objektverzeichnis',
    prSec4: 'Zahlungen pro Jahr',
    prSec5: 'Ankaufspipeline',
    prSec6: 'Prognose bis Ende',
    prSecSales: 'Realisierte Verkäufe',
    // ==================== PORTFOLIO REPORT — realised sales ====================
    prIncomeFromSelling: 'Einnahmen aus Verkäufen',
    prUnitSold: 'Einheit verkauft',
    prUnitsSold: 'Einheiten verkauft',
    prSoldLede: 'verkauft.',
    prRealisedProfit: 'Realisierter Gewinn',
    prRealisedProfitLower: 'realisierter Gewinn',
    prRealisedRoiLower: 'realisierte Rendite',
    prSaleProceedsGross: 'Verkaufspreis gesamt',
    prSellingCosts: 'Verkaufskosten',
    prNetProceeds: 'Nettoerlös',
    prAfterCosts: 'nach Transaktionskosten',
    prOfSalePrice: 'des Verkaufspreises',
    prDldCommissionTrustee: 'DLD, Provision & Trustee',
    prCapitalReturned: 'Zurückgeflossenes Kapital',
    prAtPurchasePrice: 'zum Kaufpreis',
    prRealisedNote: 'Eine verkaufte Einheit ist kein Bestand mehr und bleibt in den Zahlen oben unberücksichtigt — ihr Kapital und ihr Gewinn werden hier ausgewiesen.',
    prColSold: 'Verkauft',
    prColHeld: 'Haltedauer',
    prColSalePrice: 'Verkaufspreis',
    prColCosts: 'Kosten',
    prColNetProfit: 'Nettogewinn',
    prMonthsShort: 'Mon.',
    prDldTransferFee: 'DLD-Übertragungsgebühr',
    prAgencyCommission: 'Maklerprovision',
    prTrusteeFee: 'Trustee-Gebühr',
    prOtherExpenses: 'Weitere Kosten',
    prNoPurchaseBasis: 'kein Kaufpreis gespeichert',
    prGrossProfit: 'Bruttogewinn',
    prBeforeCosts: 'vor Verkaufskosten',
    prAnnualisedRoi: 'Jährl. ROI',
    prSaleDate: 'Verkaufsdatum',
    prSalesFoot: 'ROI = (Nettoerlös − Kaufpreis) ÷ Kaufpreis. Der Nettoerlös ist der Verkaufspreis abzüglich DLD-Übertragungsgebühr, Maklerprovision, Trustee-Gebühr und aller weiteren zum Verkauf erfassten Kosten. Der Jahreswert ist eine einfache Annualisierung dieser Rendite über die Haltedauer und wird ab drei Monaten Haltedauer angezeigt.',
    prTapSale: 'Tippen Sie auf einen Verkauf für die vollständige Kostenaufstellung.',
    // ---- Off-Plan-Verkauf (SPA-Übertragung vor Übergabe)
    prAssignmentLabel: 'Off-Plan-Übertragung',
    prOfWhichAssignmentOne: 'Einer davon war eine Off-Plan-Übertragung: der Käufer hat die restlichen Raten übernommen, daher wird die Rendite am tatsächlich eingezahlten Kapital gemessen.',
    prOfWhichAssignmentMany: 'Einige davon waren Off-Plan-Übertragungen: der Käufer hat die restlichen Raten übernommen, daher wird deren Rendite am tatsächlich eingezahlten Kapital gemessen.',
    prColCapitalIn: 'Kapital',
    prCapitalPaidIn: 'Eingezahltes Kapital',
    prCapitalAtRisk: 'tatsächlich eingesetztes Kapital',
    prOfSpaPrice: 'bei einem SPA-Preis von',
    prOnSpaPrice: 'auf den SPA-Preis',
    prOnEntryPrice: 'auf Ihren Einstiegspreis',
    prCashReceived: 'Erhaltene Auszahlung',
    prOutstandingAssumed: 'Vom Käufer übernommene Restschuld',
    prBalanceToDeveloper: 'gingen an den Entwickler',
    prNocFee: 'NOC-Gebühr (Entwickler)',
    prDeveloperAdminFee: 'Verwaltungsgebühr (Entwickler)',
    prPaidPreviousOwner: 'An den Voreigentümer gezahlt',
    prInstallmentsSince: 'Seit der Übernahme gezahlte Raten',
    prTakenOverLabel: 'von einem Voreigentümer übernommen',
    prSalesFootAssignment: 'Bei einer Off-Plan-Übertragung besteht noch kein Eigentum, daher wechselt der SPA den Besitzer: der Käufer übernimmt die Restschuld gegenüber dem Entwickler und zahlt das eingezahlte Kapital plus Aufschlag. Die erhaltene Auszahlung ist deshalb der Preis abzüglich dieser Restschuld und der Verkaufskosten, und die Rendite wird an den tatsächlich gezahlten Raten gemessen — nie am vollen SPA-Preis.',
    prSaleNoBasisOne: 'Verkauf ist ohne Kaufpreis erfasst und bleibt in der Gewinnsumme unberücksichtigt.',
    prSaleNoBasisMany: 'Verkäufe sind ohne Kaufpreis erfasst und bleiben in der Gewinnsumme unberücksichtigt.',
    // ==================== PORTFOLIO REPORT — at a glance ====================
    prTotalInvestment: 'Gesamtinvestition',
    prPropsAtPurchase: 'Immobilien zum Kaufpreis',
    prAnnualNetRent: 'Jahresnettomiete',
    prAnnualNetRentLower: 'Jahresnettomiete',
    prNoRentedYet: 'Noch keine vermieteten Einheiten',
    prSignedLease: 'unterzeichneter Mietvertrag',
    prSignedLeases: 'unterzeichnete Mietverträge',
    prRentTargetProgress: 'Fortschritt zum Mietziel',
    prSecured: 'Gesichert',
    prRentedProperty: 'vermietete Immobilie',
    prRentedProperties: 'vermietete Immobilien',
    prUnitsInPortfolio: 'Einheiten im Portfolio',
    prCompletedLower: 'fertiggestellt',
    prOffPlanLower: 'Off-Plan',
    prProgressTowardsRent: 'Fortschritt zum Mietziel von',
    prItems: 'Objekte',
    prOpenLegend: 'Offen',
    prCongrats: 'Gratulation',
    prIsLeased: 'ist vermietet.',
    prAnnualRentSecured: 'Jahresmiete gesichert',
    prOf: 'von',
    prCompletedUnit: 'fertiggestellte Einheit',
    prCompletedUnits: 'fertiggestellten Einheiten',
    prNowLeased: 'jetzt vermietet.',
    prNextPayment: 'Nächste Zahlung',
    prDue: 'fällig',
    prDivisionPaidOpen: 'Aufteilung · bezahlt vs. offen',
    prAlreadyPaid: 'Bereits bezahlt',
    prOutstanding: 'Offene Zahlungen & Übertragungen',
    prPaymentSplitHint: 'Die Aufteilung erscheint, sobald Zahlungspläne aus Ihren OTP-/SPA-Dokumenten ausgelesen wurden.',
    prMixByPurchase: 'Portfoliostruktur nach Kaufpreis',
    prObjects: 'Objekte',
    prOffPlanCap: 'Off-Plan',
    prCompletedCap: 'Fertiggestellt',
    // ==================== PORTFOLIO REPORT — capital deployment ====================
    prCommitted: 'investiert.',
    prToGoSentence: 'bis zum Ziel.',
    prProgressTowardsTarget: 'Fortschritt zum Ziel von',
    prInvestedNotarised: 'Investiert & notariell beurkundet',
    prAvailable: 'Verfügbar',
    prMilestonesCapital: 'Meilensteine · eingesetztes Kapital',
    prDeployed: 'investiert',
    prAchieved: 'erreicht',
    prOpenState: 'offen',
    prMilestonesRent: 'Meilensteine · Jahresmieteinnahmen (netto)',
    prRentPa: 'Miete p. a.',
    prEarnsLive: 'Ihr Investmentportfolio verdient — live',
    prAccruedToday: 'Heute bisher aufgelaufene Vertragsmiete',
    prPerDay: 'pro Tag',
    prPerMonthAvg: 'pro Monat (Durchschnitt)',
    prPerDayAtPre: 'pro Tag beim',
    prPerDayAtPost: 'Ziel',
    prNextMilestone: 'Nächster Meilenstein',
    prInvestedWord: 'investiert',
    prToGoShort: 'bis zum Ziel',
    prNextRentalTarget: 'Nächstes Mietziel',
    prPa: 'p. a.',
    prBasisPre: 'Basis: Jahresmiete aus Ihren',
    prSignedTenancy: 'unterzeichneten Mietvertrag',
    prSignedTenancies: 'unterzeichneten Mietverträgen',
    prBasisAccrued: 'aufgelaufen bis zur aktuellen Uhrzeit.',
    prBasisGross: 'Brutto vor Servicegebühren & Kosten · umgerechnet mit',
    prPerEur: 'pro EUR',
    // ==================== PORTFOLIO REPORT — achievements ====================
    prMilestonesAchievements: 'Meilensteine & Erfolge',
    prUnlocked: 'freigeschaltet',
    prBadgeTrophy: 'Vorzeigeobjekt',
    prSecuredSuffix: 'gesichert',
    prBadgeLargestUnit: 'Größte Einheit gesichert',
    prBadgePortfolioBasis: 'Portfolio-Grundstein',
    prUnit: 'Einheit',
    prUnits: 'Einheiten',
    prAcquired: 'erworben',
    prBadgeCommercial: 'Erstes Gewerbeobjekt',
    prBadgeOfficeVilla: 'Kauf von Büro / Villa',
    prFirst: 'Erste',
    prBadgeFirstRent: 'Erste Miete',
    prPropertySing: 'Immobilie',
    prPropertyPlur: 'Immobilien',
    prRentedSuffix: 'vermietet',
    prRentTarget: 'Mietziel',
    prBadgeHalfTime: 'Halbzeit',
    prMission: 'Mission',
    prTargetReached: 'Ziel erreicht',
    // ==================== PORTFOLIO REPORT — object register ====================
    prOneSkyline: 'Eine Skyline. Ein Ziel.',
    prColObject: 'Objekt',
    prColUnit: 'Einheit',
    prColType: 'Typ',
    prColPosition: 'Lage',
    prColHandover: 'Übergabe',
    prColPurchase: 'Kauf',
    prColStatus: 'Status',
    prProperty: 'Immobilie',
    prFinishedRented: 'Fertig · vermietet',
    prFinishedVacant: 'Fertig · leer',
    prArea: 'Fläche',
    prPricePerSqft: 'Preis / sqft',
    prPurchased: 'Gekauft',
    prMarketValueDld: 'Marktwert (DLD)',
    prMarketValue: 'Marktwert',
    prNoDldComps: 'Kaufpreis — keine DLD-Vergleichsverkäufe',
    prUnrealisedGain: 'Nicht realisierter Gewinn',
    prRentPerMonth: 'Miete / Monat',
    prEst: 'gesch.',
    prGrossYield: 'Bruttorendite',
    prTenant: 'Mieter',
    prLeased: 'Vermietet',
    prRentCollected: 'Vereinnahmte Miete',
    prExpensesActual: 'Ausgaben (tatsächlich)',
    prNetRent: 'Nettomiete',
    prInTotal: 'Gesamt',
    prTapObject: 'Tippen Sie ein Objekt an, um Fläche, Bewertung und Mietdetails zu sehen.',
    // ==================== PORTFOLIO REPORT — payments ====================
    prColYear: 'Jahr',
    prColCount: 'Anzahl',
    prColAmount: 'Betrag',
    prColPaidOfWhich: 'davon bezahlt',
    prColStillOpen: 'Noch offen',
    prInstallments: 'Raten',
    prPaid: 'Bezahlt',
    prTapYear: 'Tippen Sie ein Jahr an, um alle Raten im Detail zu sehen.',
    prNext5: 'Nächste 5 Zahlungen',
    prColDue: 'Fällig',
    prColRate: 'Rate',
    // ==================== PORTFOLIO REPORT — pipeline ====================
    prSegment: 'Segment',
    prPhase: 'Phase',
    prReviewing: 'In Prüfung',
    prOffer: 'Angebot',
    prNetReturnLower: 'Nettorendite',
    prNextSteps: 'Nächste Schritte',
    // ==================== PORTFOLIO REPORT — projection ====================
    prUntilDecember: 'Bis Dezember:',
    prRentWord: 'Miete.',
    prPerDaySentence: 'Pro Tag.',
    prInvestedByEnd: 'Investiert bis Ende',
    prAnnualRentAtEnd: 'Jahresmiete Ende',
    prMonthlyRent: 'Monatsmiete',
    prDailyRent: 'Tagesmiete',
    prColMonth: 'Monat',
    prCapitalInvested: 'Investiertes Kapital',
    prCumulativeInvestments: 'Kumulierte Investitionen',
    prAnnualRent: 'Jahresmiete',
    prCumulative: 'Kumuliert',
    prBaseline: 'Basis',
    prFootRest: 'Miete aus unterzeichneten Mietverträgen · ausgewiesen ist ausschließlich Ihre tatsächliche Mieteinnahme aus der Immobilienverwaltung — Einheiten ohne unterzeichneten Mietvertrag tragen bis zur Vermietung nichts bei · das investierte Kapital wächst mit den geplanten Raten Ihres Zahlungsplans.',
    prLtPre: 'Bei',
    prLtMid: 'und',
    prLtGoal: 'das Ziel von',
    prReachedIn: 'Erreicht im Jahr',
    prInSight: 'In Sichtweite.',
    prColPortfolio: 'Portfolio',
    prColNetReturn: 'Nettorendite',
    prGoalAchieved: 'Ziel erreicht',
    prAssumesPre: 'Annahme: Das Portfolio ist vollständig investiert bis',
    prAssumesRises: '· die Nettorendite steigt um',
    prAssumesPoints: 'Prozentpunkte pro Jahr von',
    prAssumesTo: 'auf',
    prAssumesThrough: 'durch Mietanpassungen, Neuvermietungen und Optimierung · bei konstanten',
    prAssumesStays: 'bleibt die Miete bei',
    prAssumesStep: 'der Sprung auf',
    prAssumesEnd: 'kommt aus der Rendite.',
    prFooterPre: 'Beträge umgerechnet mit',
    prFooterPost: 'pro EUR · Marktwerte aus Aufzeichnungen des Dubai Land Department, sofern verfügbar · Mieten aus unterzeichneten Mietverträgen, sofern vorhanden.',

    // ==================== SURVEY VIEW ====================
    svHeaderTitle: 'Wir freuen uns über Ihre Rückmeldung',
    svHeaderSub: 'Helfen Sie uns, die App für Sie zu verbessern – dauert etwa 3–4 Minuten',
    svProgress: 'Fortschritt',
    svOf: 'von',
    svSection1: 'Ihre Nutzung',
    svSection2: 'Herausforderungen',
    svSection3: 'Wie geht es weiter',
    svFmtOne: 'Eine Antwort wählen',
    svFmtMulti: 'Mehrfachauswahl möglich',
    svFmtRate: 'Bewertung 1–5',
    svFmtRank: 'Nach Wichtigkeit sortieren',
    svFmtVoice: 'Tippen oder Sprachnachricht aufnehmen',
    svQ1: 'Wie viele Immobilien verwalten Sie derzeit in der App?',
    svQ1Opt4: 'Mehr als 5',
    svQ2: 'Welche Bereiche der App haben Sie bereits genutzt?',
    svQ2Opt1: 'Mein Immobilienportfolio ansehen',
    svQ2Opt2: 'Dokumente hochladen',
    svQ2Opt3: 'KI-Assistent',
    svQ2Opt4: 'Zahlungsplan verfolgen',
    svQ2Opt5: 'Marktberichte',
    svQ3: 'Wie einfach lässt sich Ihre Immobilie in Dubai heute mit der App verwalten?',
    svRateLow: 'Sehr schwierig',
    svRateHigh: 'Sehr einfach',
    svQ4: 'Welche dieser Aufgaben empfinden Sie derzeit als am schwierigsten oder aufwendigsten?',
    svQ4Opt1: 'Dokumente hochladen',
    svQ4Opt2: 'Mein Portfolio auf einen Blick verstehen',
    svQ4Opt3: 'Versicherungs- und Eigentumsunterlagen im Blick behalten',
    svQ4Opt4: 'Neue Immobilien prüfen',
    svQ4Opt5: 'Wissen, was als Eigentümer als Nächstes zu tun ist',
    svRankHint: 'Tippen Sie die Punkte in Ihrer Reihenfolge an (das Wichtigste zuerst):',
    svQ5: 'Was ist für Sie die größte Herausforderung bei der Verwaltung Ihrer Immobilie?',
    svVoicePlaceholder: 'Antwort eintippen oder auf das Mikrofon tippen, um zu sprechen ...',
    svStopRecording: 'Aufnahme beenden',
    svTranscribing: 'Wird transkribiert ...',
    svRecordVoice: 'Zum Aufnehmen einer Sprachnachricht tippen',
    svQ6: 'Wobei soll die App Sie als Nächstes am meisten unterstützen?',
    svQ6Opt1: 'Besserer Überblick über alle meine Immobilien',
    svQ6Opt2: 'Einfachere Ordnung der Dokumente',
    svQ6Opt3: 'Erinnerungen an wichtige Fristen und Verlängerungen',
    svQ6Opt4: 'Bessere Entscheidungsgrundlagen vor einem weiteren Kauf',
    svQ6Opt5: 'Unterstützung beim Wiederverkauf',
    svQ7: 'Wenn die App in den nächsten 6 Monaten eine neue Funktion erhielte – welche sollte es sein?',
    svQ7Opt1: 'Bessere Portfolio-Übersicht',
    svQ7Opt2: 'Erinnerungen und Hinweise zu Dokumenten',
    svQ7Opt3: 'Unterstützung beim Wiederverkauf',
    svQ7Opt4: 'Werkzeuge zum Vergleich von Immobilien',
    svQ7Opt5: 'Beratung / Chat',
    svOther: 'Sonstiges',
    svSpecify: 'Bitte näher beschreiben ...',
    svQ8: 'Welche Verbesserung würde Ihnen persönlich am meisten Zeit oder Aufwand ersparen?',
    svQ8Opt1: 'Portfolio-Übersicht',
    svQ8Opt2: 'Dokumente und Erinnerungen',
    svQ8Opt3: 'Unterstützung beim Kauf',
    svQ8Opt4: 'Unterstützung beim Verkauf',
    svQ8Opt5: 'Unterstützung bei der Vermietung',
    svQ9: 'Fehlt Ihnen etwas in der App, das Ihnen den Immobilienbesitz erleichtern würde?',
    svSubmitting: 'Wird gesendet ...',
    svSubmitSurvey: 'Umfrage absenden',
    svConfidential: 'Ihre Antworten sind vertraulich und werden ausschließlich zur Verbesserung der App verwendet.',
    svThankYou: 'Vielen Dank!',
    svThankYouDesc: 'Ihre Rückmeldung ist für uns sehr wertvoll und fließt direkt in die nächsten Funktionen ein.',
    svRecorded: 'Ihre Antwort wurde gespeichert',
    svMicDenied: 'Bitte erlauben Sie den Zugriff auf das Mikrofon, um eine Sprachnachricht aufzunehmen.',
    svMicUnsupported: 'Sprachaufnahmen werden auf diesem Gerät nicht unterstützt.',
    svSubmitFailed: 'Die Umfrage konnte nicht gesendet werden',
    svGenericError: 'Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.',

    // ==================== CHATBOT VIEW ====================
    cbTitle: 'KI-Assistent für Dubai-Immobilien',
    cbSubtitle: 'Fachliche Einschätzungen zum Immobilienmarkt in Dubai, zu Investmentmöglichkeiten und Vorschriften',
    cbThinking: 'Die KI denkt nach ...',
    cbQuickQuestions: 'Schnelle Fragen:',
    cbQuick1: 'Welche Lagen in Dubai eignen sich am besten für Investments?',
    cbQuick2: 'Wie erhalte ich über ein Immobilieninvestment ein Visum?',
    cbQuick3: 'Wie hoch sind die aktuellen Mietrenditen?',
    cbQuick4: 'Erzählen Sie mir mehr über Off-Plan-Immobilien',
    cbPlaceholder: 'Fragen Sie zu Immobilien in Dubai ...',
    cbSend: 'Senden',
    cbHighlights: 'Wichtige Marktdaten',
    cbHighlightsSub: 'Aktuelle Trends und Einschätzungen',
    cbGrowthTitle: 'Investitionswachstum',
    cbGrowthDesc: 'Der Immobilienmarkt in Dubai wächst stark, getragen von steigenden Investitionen aus dem Ausland',
    cbVisaDesc: 'Langfristiger Aufenthalt möglich bei Immobilieninvestitionen über AED 2 Mio.',
    cbYieldTitle: 'Mietrenditen',
    cbYieldDesc: 'Attraktive Mietrenditen von durchschnittlich 5–8 % in den besten Lagen',
  },
};
